import { FC, Fragment, isValidElement, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Item, ItemProps } from ".";
import AppDropdownContent from "components/AppDropdownContent";

interface Option {
    label: ReactNode;
    value: string;
}

type SingleSelectProps = {
    multiple?: false;
    value?: string;
    onChange?: (value: string | "") => void;
};

type MultiSelectProps = {
    multiple: true;
    value?: string[];
    onChange?: (value: string[]) => void;
};

interface SelectBaseProps extends ItemProps {
    placeholder?: string;
    options: Option[];
    loading?: boolean;

    onSearch?: (keyword: string, page: number) => void | Promise<void>;

    onLoadMore?: (keyword: string, page: number) => boolean | Promise<boolean>;

    onAdd?: (keyword: string) => void;
    labelAdd?: string;
    clearable?: boolean;
    allowSearch?: boolean;
    inputClassName?: string;
}

type SelectProps = SelectBaseProps & (SingleSelectProps | MultiSelectProps);

const Select: FC<SelectProps> = ({
    label,
    required,
    col,
    placeholder = "Chọn...",
    options,
    multiple = false,
    value,
    onChange,
    error,
    loading,
    onSearch,
    onLoadMore,
    itemClassName,
    onAdd,
    labelAdd = "Thêm",
    helper,
    clearable = true,
    allowSearch = true,
    inputClassName
}) => {

    const [dropdownPos, setDropdownPos] = useState({
        top: 0,
        left: 0,
        width: 0
    });

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [dropUp, setDropUp] = useState(false);

    const firstLoadRef = useRef<boolean>(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const loadingMoreRef = useRef(false);
    function getText(node: ReactNode): string {
        if (typeof node === "string" || typeof node === "number") {
            return node.toString();
        }

        if (Array.isArray(node)) {
            return node.map(getText).join("");
        }

        if (isValidElement(node)) {
            const element = node as React.ReactElement<{ children?: ReactNode }>;
            return getText(element.props.children);
        }

        return "";
    }

    const filteredOptions = useMemo(() => {
        if (onSearch) return options;
        return options.filter(opt =>
            getText(opt.label)?.toLowerCase()?.includes(search.toLowerCase())
        );
    }, [options, search, onSearch]);

    const isSelected = (val: string) => {
        if (multiple && Array.isArray(value)) {
            return value.includes(val);
        }
        return value === val;
    };

    const handleSelect = (val: string) => {
        if (!multiple) {
            (onChange as (v: string | "") => void)?.(val);
            setOpen(false);
        } else {
            const current = Array.isArray(value) ? value : [];
            if (current.includes(val)) {
                (onChange as (v: string[]) => void)?.(
                    current.filter(v => v !== val)
                );
            } else {
                (onChange as (v: string[]) => void)?.([...current, val]);
            }
        }
    };

    const handleClear = () => {
        if (!multiple) {
            (onChange as (v: string | "") => void)?.("");
        } else {
            (onChange as (v: string[]) => void)?.([]);
        }
    };

    const handleSearch = async (keyword: string) => {
        setSearch(keyword);
        setPage(1);
        setHasMore(true);
        await onSearch?.(keyword, 1);
    };

    const handleLoadMore = async () => {
        if (!onLoadMore) return;
        if (!hasMore) return;
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        const nextPage = page + 1;
        const result = await onLoadMore(search, nextPage);
        if (result) {
            setPage(nextPage);
        } else {
            setHasMore(false);
        }
        loadingMoreRef.current = false;
    };

    const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const reachedBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
        if (reachedBottom) {
            await handleLoadMore();
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
            //     setOpen(false);
            //     setSearch("");
            // }

            const target = e.target as Node;

            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                searchRef.current?.focus();
            }, 50);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropUp(spaceBelow < 260);
    }, [open]);

    const focusNextFromTrigger = () => {
        if (!wrapperRef.current) return;

        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>(
                `
            input:not([disabled]),
            button:not([disabled]),
            textarea:not([disabled]),
            select:not([disabled]),
            [tabindex]:not([tabindex="-1"])
            `
            )
        ).filter(el => {
            return el.offsetParent !== null;
        });

        const index = focusable.indexOf(wrapperRef.current);

        if (index >= 0 && index < focusable.length - 1) {
            focusable[index + 1].focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIndex(i =>
                Math.min(i + 1, filteredOptions.length - 1)
            );
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex(i =>
                Math.max(i - 1, 0)
            );
        }
        if (e.key === "Enter") {
            e.preventDefault();
            const option = filteredOptions[highlightIndex];
            if (option) {
                handleSelect(option.value);
            }
        }

        if (e.key === "Escape") {
            setOpen(false);
        }

        if (e.key === "Tab") {
            // setOpen(false);
            // focusNextFromTrigger();
            e.preventDefault();

            setOpen(false);

            requestAnimationFrame(() => {
                focusNextFromTrigger();
            });
        }
    };

    useEffect(() => {
        if (!onSearch) return;
        if (firstLoadRef.current) return;
        // if (options.length > 0) return;
        firstLoadRef.current = true;
        setPage(1);
        setHasMore(true);
        onSearch("", 1);
    }, [onSearch]);

    const showAddButton = useMemo(() => {
        if (!onAdd) return false;
        if (!search.trim()) return false;
        return filteredOptions.length === 0;
    }, [filteredOptions.length, onAdd, search]);

    const updatePosition = () => {
        if (!wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const shouldDropUp = spaceBelow < 260;
        setDropUp(shouldDropUp);
        setDropdownPos({
            top: shouldDropUp ? rect.top : rect.bottom,
            left: rect.left,
            width: rect.width
        });
    };

    useEffect(() => {
        if (!open) return;
        let frameId: number;
        const loop = () => {
            updatePosition();
            frameId = requestAnimationFrame(loop);
        };
        updatePosition();
        frameId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frameId);
    }, [open]);

    const _inputClassName = useMemo(() => {
        let className = `@app-form-select w-full border px-4 py-1 rounded bg-white flex items-center cursor-pointer focus:outline-none hover:border-blue-500`;
        if (error) {
            className += " border-red-500";
        }
        else {
            if (open) {
                className += " border-blue-500";
            }
            else {
                className += " border-gray-300";
            }
        }
        if (inputClassName) {
            className += ` ${inputClassName}`;
        }
        return className;
    }, [error, inputClassName, open])
    return (
        <Item
            col={col}
            label={label}
            required={required}
            error={error}
            itemClassName={itemClassName}
            helper={helper}
        >
            <div
                className="relative group/select"
                ref={wrapperRef}
                onKeyDown={handleKeyDown}
            >
                <div
                    className={_inputClassName}
                    tabIndex={0}
                    role="combobox"
                    aria-expanded={open}
                    onFocus={(e) => {
                        // e.currentTarget.blur();
                        setOpen(o => {
                            return !o;
                        });
                    }}
                >
                    {multiple ? (
                        Array.isArray(value) && value.length > 0 ? (
                            <Fragment>
                                <span className="flex-1 flex flex-wrap gap-2">
                                    {value.map(v => {
                                        const label = options.find(opt => opt.value === v)?.label || v;
                                        return (
                                            <span
                                                key={v}
                                                className="px-4 py-1 rounded-full bg-gray-200 inline-block"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelect(v);
                                                }}
                                            >
                                                {label}
                                            </span>
                                        );
                                    })}
                                </span>
                                {clearable && <button
                                    type="button"
                                    className="ml-auto p-3 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 opacity-0 group-hover/select:opacity-100 transition"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleClear();
                                    }}
                                >
                                    ✕
                                </button>}
                            </Fragment>
                        ) : (
                            <span className="text-gray-400 py-1">{placeholder}</span>
                        )
                    ) : value ? (
                        <Fragment>
                            <span className="py-1">
                                {options.find(opt => opt.value === value)?.label || value}
                            </span>

                            {clearable && <button
                                type="button"
                                className="ml-auto p-3 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 opacity-0 group-hover/select:opacity-100 transition"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                ✕
                            </button>}

                        </Fragment>
                    ) : (
                        <span className="text-gray-400 py-1">{placeholder}</span>
                    )}
                </div>
                <AppDropdownContent open={open} triggerRef={wrapperRef} onClose={() => setOpen(false)} >
                    <div className="rounded bg-white shadow-2xl border border-gray">
                        {allowSearch && <div className="p-2">
                            <input
                                autoFocus
                                ref={searchRef}
                                type="text"
                                value={search}
                                placeholder="Tìm kiếm..."
                                className="w-full border px-2 py-1 rounded focus:outline-none focus:border-blue-500 bg-gray-100"
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>}

                        <div
                            ref={listRef}
                            className="overflow-auto max-h-72"
                            onScroll={handleScroll}
                        >

                            {filteredOptions.map((opt, index) => {

                                const selected = isSelected(opt.value);
                                const highlighted = index === highlightIndex;

                                return (
                                    <div
                                        key={opt.value}
                                        className={`px-4 py-2 flex items-start justify-between cursor-pointer
                                        hover:bg-gray-100
                                        ${selected ? "bg-blue-100 hover:bg-blue-100" : ""}
                                        ${highlighted ? "bg-gray-100" : ""}`}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        <span>{opt.label}</span>

                                        {selected && (
                                            <span className="text-blue-500 ml-2">✓</span>
                                        )}
                                    </div>
                                );

                            })}
                            {filteredOptions.length === 0 && (
                                <div className="px-4 py-2 text-gray-400">
                                    Không có kết quả
                                </div>
                            )}
                            {loading && (
                                <div className="text-center p-2 text-gray-400">
                                    Đang tải...
                                </div>
                            )}

                            {showAddButton && (
                                <div
                                    className="px-4 py-2 cursor-pointer text-blue-600 hover:bg-blue-50 border-t"
                                    onClick={() => onAdd?.(search)}
                                >
                                    {labelAdd}: <b>{search}</b>
                                </div>
                            )}

                        </div>
                    </div>
                </AppDropdownContent>
            </div>
        </Item>
    );
};

export default Select;